import { DragStart, DropResult } from '@hello-pangea/dnd';
import { DateTime } from 'luxon';
import { useState } from 'react';

import { ViewTable } from '@/configs/ViewTable';
import { useDraftTimelogStore } from '@/features/draftTimelogs';
import { normalizeToTime } from '@/features/log/form/utils';
import { modalManagerStore } from '@/features/ModalManagerStore';
import { useTaskStore } from '@/features/Task';
import { ModeDnd, viewSettingsStore } from '@/features/viewSettings';
import { useTimeLogStore } from '@/pages/TimesheetsPage/model';

export const useDrag = () => {
  const { view, dndMode } = viewSettingsStore;
  const { favoriteTasks } = useTaskStore();
  const { filterData: timesheetData, logOperations } = useTimeLogStore();
  const draftStore = useDraftTimelogStore();
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingFromFavorites, setIsDraggingFromFavorites] = useState(false);
  const [isHoveringOverFavorites, setIsHoveringOverFavorites] = useState(false);

  const onDragStart = (start: DragStart) => {
    setIsDragging(true);
    setIsDraggingFromFavorites(start.source.droppableId === 'favoriteTasks');
  };

  const handleFavoriteTaskToDay = (draggableId: string, destinationId: string) => {
    const task = favoriteTasks.find((t) => t.id.toString() === draggableId);

    if (!task) return;

    const targetDate = DateTime.fromISO(destinationId);

    if (!targetDate.isValid) return;

    modalManagerStore.open('addLog', {
      date: targetDate,
      taskKey: task.key,
      taskTitle: task.summary,
      hasPreselectedDate: true,
      isTaskSelected: true,
      isFromFavorites: true,
    });
  };

  const handleLogMove = (movedLog: any, destinationId: string) => {
    if (dndMode === ModeDnd.Moving) {
      const startTime = DateTime.fromISO(movedLog.start).toFormat('HH:mm');
      const startDateWithTimezone = normalizeToTime(destinationId, startTime);

      logOperations.updateLog({
        workLogId: movedLog.id,
        taskKey: movedLog.taskKey,
        duration: movedLog.durationMinutes,
        comment: movedLog.comment ?? '',
        startDate: startDateWithTimezone,
      });

      return;
    }

    if (dndMode === ModeDnd.Duplication) {
      modalManagerStore.open('copyLog', {
        log: movedLog,
        day: DateTime.fromISO(destinationId),
      });
    }
  };

  const handleWeekViewDrag = (sourceId: string, destinationId: string, draggableId: string) => {
    if (view !== ViewTable.Week) return;

    const movedLog = timesheetData?.days[sourceId]?.find(
      (log) => log.id.toString() === draggableId,
    );

    if (!movedLog) return;

    handleLogMove(movedLog, destinationId);
  };

  const onDragEnd = (result: DropResult) => {
    setIsDragging(false);
    setIsDraggingFromFavorites(false);
    setIsHoveringOverFavorites(false);
    const { source, destination, draggableId } = result;

    if (!destination) return;

    if (destination.droppableId === 'favoriteTasks') return;

    if (source.droppableId === destination.droppableId) return;

    if (source.droppableId === 'favoriteTasks') {
      handleFavoriteTaskToDay(draggableId, destination.droppableId);

      return;
    }

    if (draggableId.startsWith('draft-')) {
      const draftId = draggableId.replace('draft-', '');
      const draft = draftStore.drafts.find((d) => d.id === draftId);

      if (draft) {
        const targetDate = DateTime.fromISO(destination.droppableId);

        if (targetDate.isValid) {
          const oldStart = DateTime.fromISO(draft.start);
          const newStart = targetDate.set({
            hour: oldStart.hour,
            minute: oldStart.minute,
            second: 0,
            millisecond: 0,
          });

          if (dndMode === ModeDnd.Moving) {
            draftStore.updateDraft(draftId, { start: newStart.toISO()! });
          } else if (dndMode === ModeDnd.Duplication) {
            draftStore.createDraft({
              duration: draft.duration,
              start: newStart.toISO()!,
              comment: draft.comment,
              taskKey: draft.taskKey,
              taskTitle: draft.taskTitle,
              queueKey: draft.queueKey,
              queueName: draft.queueName,
            });
          }
        }
      }

      return;
    }

    handleWeekViewDrag(source.droppableId, destination.droppableId, draggableId);
  };

  return {
    isDragging,
    isDraggingFromFavorites,
    isHoveringOverFavorites,
    setIsHoveringOverFavorites,
    onDragStart,
    onDragEnd,
  };
};
